// Shared, app-wide session player. Owns "what's playing right now" so a
// persistent bottom player bar can reflect and control it from anywhere, not
// just the Session view.
//
// It routes playback by session type (the same rules as the Session page):
//   - Looping session, logged-in  -> client jam engine (layered, in sync)
//   - Looping session, logged-out -> one server 2ch mixdown stream
//   - Playlist session            -> loops play sequentially
//
// State is a Vue `reactive` object so components can bind to it directly.
import { reactive } from "vue";
import store from "@/store";
import router from "@/router";
import jamEngine from "./jamEngine";
import { loopMeta, loadLoopMeta } from "./loopMeta";
import nowPlaying from "./nowPlaying.js";
import { sessionTitle } from "@/kit/sessionName.js";
import { displayKey } from "@/kit/musicKey.js";
import { forName } from "@/kit/people.js";
import {
  getMixdownUrlForSession,
  getStreamUrlFromProxyWithId,
  getWavDownloadFromProxyWithId,
} from "@/filters/utils";

const state = reactive({
  session: null, // the session doc currently loaded into the player
  isPlaying: false,
  buffering: false, // loading/decoding/awaiting the first audible frame
  error: null, // last playback error, surfaced to the UI
  mode: null, // 'jam' (Web Audio) | 'audio' (<audio> element)
  playlistIndex: 0,
  currentHash: null, // loop shown in the bar / playlist highlight
  progress: 0, // 0-100 played fraction of the current <audio>-driven track
  // Seconds into the current track or mix cycle. A looping session's mix
  // tiles every loop from zero, so a layer's own phase is this position
  // modulo its length (layerProgress).
  position: 0,
});

let audio = null; // shared <audio> for mixdown + sequential playback

// Streamed audio (the ffmpeg webm mixdown, and cold loop streams) has no
// trustworthy audio.duration until fully buffered: Chrome reports Infinity, and
// Firefox reports a finite estimate that keeps growing as it buffers (so a
// cursor dividing by it jumps and drifts). Use a duration fetched from loop
// metadata instead — the same trick the WaveformPlayer uses (loopAudio.js).
let knownDuration = 0; // seconds; progress denominator (preferred over audio.duration)
let durationSeq = 0; // bumped per track so a late fetch can't apply to a new one
let rafId = null;
let jamPhaseKey = null; // jamEngine track whose phase drives the jam-mode cursor
// Buffer sources can't pause, so a paused jam restarts on resume. This is the
// position (seconds) it restarts from — set by pause() and by seeking while paused.
let jamResumeAt = 0;
// Bumped by every start, pause and stop. A start's async tail (the mix's
// decode, an <audio> play() promise) that finds it moved was overtaken, by
// a preview pausing Play all, say, and does nothing: no sources, no
// <audio> fallback, no state.
let playGen = 0;

const API = process.env.VUE_APP_API_URL || "";

function effectiveDuration() {
  if (knownDuration > 0) return knownDuration;
  if (audio && isFinite(audio.duration) && audio.duration > 0) {
    return audio.duration;
  }
  return 0;
}

function updateProgress() {
  if (state.mode === "jam") {
    // Web Audio has no <audio> timeline; show the looped buffer's phase.
    const ph = jamEngine.loopPhase(jamPhaseKey);
    if (ph != null) {
      state.progress = ph * 100;
      state.position = ph * jamEngine.duration(jamPhaseKey);
    }
    return;
  }
  const d = effectiveDuration();
  if (!audio || d <= 0) return;
  state.progress = Math.min((audio.currentTime / d) * 100, 100);
  state.position = audio.currentTime % d;
}

function progressLoopActive() {
  return state.mode === "jam"
    ? state.isPlaying && jamEngine.playing
    : !!(audio && !audio.paused);
}

// Sample position every animation frame while playing — timeupdate only fires
// ~4x/s (and never for the jam engine), which reads as a stepping cursor.
function startProgressLoop() {
  if (rafId) return;
  const tick = () => {
    rafId = null;
    if (!progressLoopActive()) return;
    updateProgress();
    rafId = requestAnimationFrame(tick);
  };
  rafId = requestAnimationFrame(tick);
}
function stopProgressLoop() {
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
}

async function fetchLoopDuration(hash) {
  try {
    const resp = await fetch(`${API}/api/loop?id=${hash}`);
    const body = await resp.json();
    const d = Number(body?.data?.results?.duration);
    return isFinite(d) && d > 0 ? d : 0;
  } catch (_) {
    return 0;
  }
}

// Resolve the fallback duration for the track that's starting now. `promise`
// yields seconds; a stale resolution (track changed meanwhile) is dropped.
function resolveKnownDuration(promise) {
  const seq = ++durationSeq;
  knownDuration = 0;
  Promise.resolve(promise).then((d) => {
    if (seq === durationSeq && d > 0) {
      knownDuration = d;
      updateProgress();
    }
  });
}

function ensureAudio() {
  if (!audio) {
    audio = new Audio();
    // Fallback cadence for throttled background tabs where rAF stalls.
    audio.addEventListener("timeupdate", updateProgress);
    // Buffering/playing/error come straight from the element so the UI reflects
    // the real stream state (esp. the ffmpeg mixdown, which takes a moment to
    // start). 'playing' is the first-audible-frame signal.
    audio.addEventListener("waiting", () => {
      state.buffering = true;
    });
    audio.addEventListener("stalled", () => {
      state.buffering = true;
    });
    audio.addEventListener("playing", () => {
      state.buffering = false;
      state.error = null;
      state.isPlaying = true;
      startProgressLoop();
    });
    audio.addEventListener("error", () => {
      state.buffering = false;
      state.isPlaying = false;
      state.error = "Couldn't play this stream.";
    });
  }
  return audio;
}
function firstHash(session) {
  const l = loopsOf(session)[0];
  return l ? l.hash : null;
}
function loopsOf(session) {
  return (session?.library || []).filter((l) => l && l.hash);
}
function isLoopingSession() {
  return !!(state.session && state.session.is_looping);
}

// A short signature of the session's loops. Appended to the mixdown URL so a
// loop added mid-session busts the cached (stale) mix instead of replaying it.
function libraryVersion(session) {
  const key = loopsOf(session)
    .map((l) => l.hash)
    .join(",");
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36) + "-" + loopsOf(session).length;
}

// The bar's item for the loaded session (SIGNED_IN_PLAN 3.5): its title,
// "N loops · <creator>", tempo and key, and "Open session" (tinted; the bar
// hides it on that session's own page). A playlist session's prev and next
// step through its loops; a looping one has neither.
function sessionItem() {
  const s = state.session;
  if (!s) return null;
  const n = loopsOf(s).length;
  const names = s.member_display_names || {};
  const creator = (names[s.creator] && String(names[s.creator]).trim()) || s.creator || "";
  const me = store.state.loggedInUser?.username || null;
  const tempo = Number(s.tempo_bpm) > 0 ? `${Math.round(Number(s.tempo_bpm))} BPM` : null;
  const key = displayKey(s.key || s.musical_key || "") || null;
  const color = forName(s.creator || "", me);
  const API = process.env.VUE_APP_API_URL || "";
  return {
    id: `session:${s.name}`,
    sessionName: s.name,
    title: sessionTitle(s),
    subtitle: [`${n} ${n === 1 ? "loop" : "loops"}`, creator].filter(Boolean).join(" · "),
    art: { seed: s.name, owner: color, picture: s.picture && String(s.picture).startsWith("http") ? s.picture : null },
    color,
    waveform: {
      src: `${API}/download/spectrogram/session/${encodeURIComponent(s.name)}?width=1400&height=42&type=waveform`,
    },
    meta: [tempo, key].filter(Boolean).join(" · "),
    cta: {
      label: "Open session",
      kind: "tinted",
      disabled: false,
      onClick: () => router.push(`/session/${encodeURIComponent(s.name)}`).catch(() => {}),
    },
    addToSession: null,
    dragHandle: null,
    pricePill: null,
    share: null,
  };
}

const player = {
  state,

  hasSession() {
    return !!(state.session && loopsOf(state.session).length);
  },
  loops() {
    return loopsOf(state.session);
  },
  isLooping() {
    return isLoopingSession();
  },

  // True when the player is actively playing the named session. Lets the
  // Session page show its own play/pause state without owning playback, so a
  // session keeps playing (in the bar) while you browse elsewhere.
  isPlayingSession(name) {
    return !!(
      state.isPlaying &&
      state.session &&
      state.session.name === name
    );
  },

  // Where one layer of the named session is, 0-100, while the player holds
  // that session (playing or paused); null when the layer isn't sounding.
  // Play all: each loop's phase in the mix (position modulo its length, once
  // its length is known). A playlist session: only the loop on now.
  layerProgress(name, hash) {
    if (!state.session || state.session.name !== name || !state.mode) return null;
    if (!isLoopingSession()) {
      return state.currentHash === hash ? state.progress : null;
    }
    const d = loopMeta(hash)?.duration || 0;
    if (!(d > 0)) return null;
    return ((state.position % d) / d) * 100;
  },

  // Load a specific session and start it (stopping whatever played before).
  // The same session takes its latest doc too: a play after loops landed
  // mixes them in (it used to replay the mix it first loaded).
  playSession(session) {
    if (!session) return;
    const changed = !state.session || session.name !== state.session.name;
    if (changed) this.stop();
    state.session = session;
    this.play();
  },

  // The open session's doc changed (a state_update, or a resync): a loop
  // landed or went, or a rename. Keep the loaded doc current so every play
  // has every loop, and while Play all loops, sequence the change in: the
  // new mix starts where the old one is, on the shared clock (swapMix). A
  // playlist session reads its loops per track, so it picks them up as is.
  // The <audio> fallback keeps playing; its next play has the new mix.
  sessionUpdated(session) {
    if (!session || !state.session || session.name !== state.session.name) return;
    const before = libraryVersion(state.session);
    const had = new Set(loopsOf(state.session).map((l) => l.hash));
    state.session = session;
    if (libraryVersion(session) === before) return;
    if (!loopsOf(session).length) {
      this.stop();
      return;
    }
    const added = loopsOf(session).map((l) => l.hash).filter((h) => !had.has(h));
    loadLoopMeta(loopsOf(session).map((l) => l.hash));
    if (isLoopingSession() && state.mode === "jam" && state.isPlaying && !state.buffering) {
      this._swapMix(added);
    }
  },

  // `added`: the loops that landed. The switch waits for the next cycle of
  // the longest of them, so each enters from its frame 0, as the app
  // launches a new layer (swapMix); with none (a loop went) it switches
  // now; unknown (null: a mix that went stale while decoding), at the new
  // mix's next wrap, where every layer is at its frame 0.
  _swapMix(added = null) {
    const toKey = "__mix__" + libraryVersion(state.session);
    if (toKey === jamPhaseKey) return;
    const url = getMixdownUrlForSession(
      state.session.name,
      store.getters.mediaLibraryToken,
      libraryVersion(state.session),
      "wav",
    );
    const longest = () => Math.max(0, ...added.map((h) => loopMeta(h)?.duration || 0));
    const alignTo = !added ? () => 0 : added.length ? longest : () => -1;
    jamEngine.swapMix(toKey, url, { alignTo }).then((ok) => {
      // false: paused, stopped or superseded meanwhile (a later play
      // loads the latest mix anyway).
      if (ok) jamPhaseKey = toKey;
    });
  },

  // The bar's item for the loaded session.
  item() {
    return sessionItem();
  },

  play() {
    const loops = loopsOf(state.session);
    if (!loops.length) return;
    // Play all takes the bar and stops any audition (SI-4).
    nowPlaying.sessionStarted(sessionItem());
    if (isLoopingSession()) {
      this._playLooping();
    } else {
      this._playSequential(0);
    }
  },

  // Gapless-first looping: decode a single pre-mixed WAV and loop it in an
  // AudioContext (sample-accurate, no seam). If the context can't start — iOS
  // sometimes leaves it suspended even after a gesture — or the mix can't load,
  // fall back to the <audio> element mixdown, which always plays but has a small
  // loop seam. Best of both: gapless where possible, never silent.
  _playLooping(offset = 0) {
    jamEngine.stop();
    this._stopAudioEl();
    stopProgressLoop();
    durationSeq += 1;
    jamResumeAt = 0;
    state.mode = "jam";
    state.currentHash = firstHash(state.session);
    // Resuming mid-mix keeps the cursor where it was instead of flashing to 0.
    if (!offset) {
      state.progress = 0;
      state.position = 0;
    }
    state.buffering = true;
    state.error = null;
    // Key the buffer by the library version so a loop added mid-session loads a
    // fresh mix instead of replaying the cached decoded one.
    const mixKey = "__mix__" + libraryVersion(state.session);
    jamPhaseKey = mixKey;
    const url = getMixdownUrlForSession(
      state.session.name,
      store.getters.mediaLibraryToken,
      libraryVersion(state.session),
      "wav",
    );
    state.isPlaying = true;
    const gen = ++playGen;
    jamEngine
      .play([{ hash: mixKey, url }], offset)
      .then((ok) => {
        if (gen !== playGen) return;
        if (ok && jamEngine.isRunning()) {
          state.buffering = false;
          startProgressLoop();
          // A loop landed while this mix decoded: sequence it in now.
          if (jamPhaseKey !== "__mix__" + libraryVersion(state.session)) this._swapMix();
        } else {
          // Web Audio produced no sound (iOS blocked the context) — fall back.
          this._playMixdownAudio();
        }
      })
      .catch(() => {
        if (gen === playGen) this._playMixdownAudio();
      });
  },

  _playJam(loops) {
    this._stopAudioEl();
    stopProgressLoop();
    durationSeq += 1; // drop any pending duration fetch from a prior track
    state.mode = "jam";
    // The layered mix has no single "current" loop; show the first as the bar's
    // waveform and drive the cursor from its phase.
    state.currentHash = loops[0] ? loops[0].hash : null;
    jamPhaseKey = state.currentHash;
    state.progress = 0;
    const items = loops.map((l) => ({
      hash: l.hash,
      url: `${getWavDownloadFromProxyWithId(
        l.hash,
        store.getters.mediaDownloadToken(l.hash),
      )}&play=1`,
    }));
    state.isPlaying = true;
    const gen = ++playGen;
    jamEngine
      .play(items)
      .then((ok) => {
        if (gen !== playGen) return;
        state.isPlaying = ok;
        if (ok) startProgressLoop();
      })
      .catch(() => {
        if (gen === playGen) state.isPlaying = false;
      });
  },

  // Fallback path: the whole-session mixdown played through one <audio> element
  // (AAC/WebM, range-served). Reliable everywhere but not gapless.
  _playMixdownAudio() {
    jamEngine.stop();
    state.mode = "audio";
    state.currentHash = firstHash(state.session);
    state.progress = 0;
    state.position = 0;
    state.buffering = true; // ffmpeg mix takes a moment to start; show a loader
    state.error = null;
    // The mixdown tiles to the longest loop (server-capped at 120s), then the
    // element loops it — so its period is that max duration. Match it so the
    // cursor cycles correctly even while duration reports Infinity.
    const loops = loopsOf(state.session);
    resolveKnownDuration(
      Promise.all(loops.map((l) => fetchLoopDuration(l.hash))).then((ds) =>
        Math.min(Math.max(0, ...ds), 120),
      ),
    );
    const a = ensureAudio();
    a.loop = true;
    a.onended = null;
    // Cache-bust by the library signature so a loop added mid-session doesn't
    // replay a stale cached mix.
    a.src = getMixdownUrlForSession(
      state.session.name,
      store.getters.mediaLibraryToken,
      libraryVersion(state.session),
    );
    state.isPlaying = true; // optimistic; the 'playing' event clears buffering
    const gen = ++playGen;
    a.play().catch(() => {
      if (gen !== playGen) return; // paused or replaced meanwhile
      state.buffering = false;
      state.isPlaying = false;
      state.error = "Tap play to start audio.";
    });
  },

  _playSequential(index) {
    jamEngine.stop();
    state.mode = "audio";
    state.playlistIndex = index;
    this._playPlaylistItem();
  },

  _playPlaylistItem() {
    const loops = loopsOf(state.session);
    if (!loops.length) return;
    if (state.playlistIndex >= loops.length) state.playlistIndex = 0;
    if (state.playlistIndex < 0) state.playlistIndex = loops.length - 1;
    const loop = loops[state.playlistIndex];
    state.currentHash = loop.hash;
    state.progress = 0;
    state.buffering = true;
    state.error = null;
    resolveKnownDuration(fetchLoopDuration(loop.hash));
    const a = ensureAudio();
    a.loop = false;
    a.src = getStreamUrlFromProxyWithId(
      loop.hash,
      // Owner's download token unlocks the clean preview; else stream token.
      store.getters.mediaDownloadToken(loop.hash) ||
        store.getters.mediaStreamToken(loop.hash),
    );
    a.onended = () => {
      state.playlistIndex += 1;
      this._playPlaylistItem();
    };
    state.isPlaying = true; // optimistic; the 'playing' event clears buffering
    const gen = ++playGen;
    a.play().catch(() => {
      if (gen !== playGen) return; // paused or replaced meanwhile
      state.buffering = false;
      state.isPlaying = false;
      state.error = "Tap play to start audio.";
    });
  },

  // Playlist transport.
  next() {
    if (isLoopingSession()) return;
    state.playlistIndex += 1;
    this._playPlaylistItem();
  },
  prev() {
    if (isLoopingSession()) return;
    state.playlistIndex -= 1;
    this._playPlaylistItem();
  },
  playTrackAt(index) {
    if (isLoopingSession()) return;
    this._playSequential(index);
  },

  toggle() {
    if (state.isPlaying) this.pause();
    else this.resume();
  },

  pause() {
    playGen += 1; // a start still loading never sounds
    if (state.mode === "jam") {
      // Web Audio buffer sources can't pause/resume — remember where we were,
      // then silence them; resume() restarts from here.
      jamResumeAt = jamEngine.position(jamPhaseKey) || 0;
      jamEngine.stop();
    } else if (audio) {
      audio.pause();
    }
    stopProgressLoop();
    state.buffering = false;
    state.isPlaying = false;
  },

  resume() {
    if (state.session) nowPlaying.sessionStarted(sessionItem());
    if (state.mode === "audio" && audio && audio.src) {
      const gen = ++playGen;
      audio
        .play()
        .then(() => {
          if (gen !== playGen) return;
          state.isPlaying = true;
          startProgressLoop();
        })
        .catch(() => {
          if (gen === playGen) state.isPlaying = false;
        });
    } else if (state.mode === "jam" && isLoopingSession()) {
      // Paused mixdown — restart where it was paused (or seeked to).
      this._playLooping(jamResumeAt);
    } else {
      // Nothing loaded yet — start from the top.
      this.play();
    }
  },

  stop() {
    playGen += 1;
    nowPlaying.sessionStopped();
    jamEngine.reset();
    this._stopAudioEl(true);
    stopProgressLoop();
    durationSeq += 1; // invalidate any in-flight duration fetch
    knownDuration = 0;
    jamResumeAt = 0;
    state.isPlaying = false;
    state.buffering = false;
    state.error = null;
    state.mode = null;
    state.currentHash = null;
    state.playlistIndex = 0;
    state.progress = 0;
    state.position = 0;
  },

  // Scrub the current track. The Web Audio mixdown jumps its sources (or, when
  // paused, moves the resume point). <audio> playback uses the effective
  // duration so scrubbing works on cold chunked streams too; a live stream
  // that isn't seekable yet simply ignores the set.
  seek(frac) {
    const f = Math.max(0, Math.min(1, frac));
    if (state.mode === "jam") {
      const jd = jamEngine.duration(jamPhaseKey);
      if (jd <= 0) return; // mix not decoded yet
      if (!jamEngine.seek(f * jd)) jamResumeAt = f * jd;
      state.progress = f * 100;
      state.position = f * jd;
      return;
    }
    const d = effectiveDuration();
    if (!audio || d <= 0) return;
    try {
      audio.currentTime = f * d;
      updateProgress();
    } catch (_) {
      // Not seekable yet (cold chunked stream) — ignore.
    }
  },

  // Stop and forget the session too (signing out: nothing of the last
  // account stays loaded).
  unload() {
    this.stop();
    state.session = null;
  },

  _stopAudioEl(clearSrc) {
    if (!audio) return;
    audio.onended = null;
    audio.pause();
    if (clearSrc) {
      audio.removeAttribute("src");
      audio.load();
    }
  },

  // Per-track mixing (looping jam only).
  setVolume(hash, v) {
    jamEngine.setVolume(hash, v);
  },
  setMuted(hash, muted) {
    jamEngine.setMuted(hash, muted);
  },
  setSolo(hash) {
    jamEngine.setSolo(hash);
  },
};

nowPlaying.registerEngine("session", {
  playing: () => state.isPlaying,
  buffering: () => state.buffering,
  progress: () => state.progress,
  loaded: () => 0,
  hasSession: () => !!state.session && !!state.mode,
  item: () => sessionItem(),
  canPrev: () => !!state.session && !isLoopingSession() && loopsOf(state.session).length > 1,
  canNext: () => !!state.session && !isLoopingSession() && loopsOf(state.session).length > 1,
  // A looping session's Play all has no ⏮ ⏭ (3.5; master's bar hid them).
  hasSteps: () => !isLoopingSession(),
  toggle: () => player.toggle(),
  pause: () => player.pause(),
  resume: () => player.resume(),
  stop: () => player.stop(),
  unload: () => player.unload(),
  seek: (f) => player.seek(f),
  next: () => player.next(),
  prev: () => player.prev(),
});

export default player;
